/**
 * Shared types, constants, and pure helper functions used across the
 * SchemaFilterBuilder sub-components (FilterConditionRow, GroupCard, etc.)
 * and the top-level SchemaFilterBuilder component.
 */

import type { SelectItemType } from '@/lib/base/forms/select/select';
import type { FilterCondition, OperatorType } from '@/lib/crm/filters/types';
import type { FieldTypeSchema } from '@/lib/crm/hooks/useFilterSchema';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import type { FilterValue } from '@/types/crm';
import { isRelativeDateValue } from '@/types/reports';

/**
 * Map backend operators to frontend operator types
 */
export const BACKEND_TO_FRONTEND_OPERATOR: Record<string, OperatorType> = {
  exact: 'equals',
  ne: 'not_equals',
  contains: 'contains',
  not_contains: 'not_contains',
  contains_any: 'contains_any',
  not_contains_any: 'not_contains_any',
  startswith: 'starts_with',
  endswith: 'ends_with',
  gt: 'greater_than',
  lt: 'less_than',
  gte: 'greater_than_or_equal',
  lte: 'less_than_or_equal',
  isnull: 'is_empty',
  in: 'in',
  notin: 'not_in',
};

/**
 * Get display label for operator
 */
// Sentence case throughout (tone guide): condition rows read as a sentence —
// "Amount · at least · 500" — so Title Case operators broke the flow.
export const OPERATOR_LABELS: Record<OperatorType, string> = {
  equals: 'Equals',
  not_equals: 'Not equals',
  contains: 'Contains',
  not_contains: 'Does not contain',
  contains_any: 'Contains any of',
  not_contains_any: 'Does not contain any of',
  starts_with: 'Starts with',
  ends_with: 'Ends with',
  greater_than: 'Greater than',
  less_than: 'Less than',
  // "At least"/"At most" over the literal "Greater than or equal"/"Less than
  // or equal": clearer as a sentence ("Amount at least 500") and short enough
  // for the compact side-by-side operator/value line.
  greater_than_or_equal: 'At least',
  less_than_or_equal: 'At most',
  is_empty: 'Is empty',
  is_not_empty: 'Is not empty',
  in: 'Is one of',
  not_in: 'Is not one of',
};

// Association-specific operators (subset that backend supports for associations)
// Note: Backend only accepts: exact, ne, in, notin, isnull
// We map is_empty/is_not_empty to isnull with appropriate boolean values
export type AssociationOperatorType =
  | 'equals'
  | 'not_equals'
  | 'in'
  | 'not_in'
  | 'is_empty'
  | 'is_not_empty';

// Human-readable labels for association operators (sentence case, see above)
export const ASSOCIATION_OPERATOR_LABELS: Record<
  AssociationOperatorType,
  string
> = {
  equals: 'Is',
  not_equals: 'Is not',
  in: 'Is any of',
  not_in: 'Is not any of',
  is_empty: 'Is unknown',
  is_not_empty: 'Is known',
};

export const ASSOCIATION_FILTER_OPERATORS: AssociationOperatorType[] = [
  'equals',
  'not_equals',
  'in',
  'not_in',
  'is_empty',
  'is_not_empty',
];

// A filter clause's value count is an operator property, not an association
// cardinality property: `in`/`not_in` take multiple values, the others one.
// Map that to the picker's selectionMode so single-valued associations still
// allow multi-select under Is Any Of / Is Not Any Of.
export function selectionModeForAssociationOperator(
  operator: AssociationOperatorType
): 'single' | 'multiple' {
  return operator === 'in' || operator === 'not_in' ? 'multiple' : 'single';
}

/**
 * A section of combobox items with a header label. Used to group properties
 * vs association-property items in the field picker.
 */
export interface FieldSection {
  key: string;
  label: string;
  items: SelectItemType[];
}

/** Stable summary of a group used to populate the "move condition" menu. */
export interface GroupSummary {
  id: string;
  label: string;
}

// Matches CardFieldPicker's SECTION_HEADER_CLASS for consistent section header styling.
// `sticky -top-1` compensates for the Popover's py-1 padding so the header
// covers the full scroll viewport. `transform-gpu` forces a compositing layer
// to ensure items don't paint above it.
export const SECTION_HEADER_CLASS =
  'sticky -top-1 z-10 transform-gpu block px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-tertiary)] bg-primary';

/**
 * Get available frontend operators for a property by looking up its field type
 */
export function getAvailableOperators(
  property: PropertyTypeSchema,
  fieldTypeMap: Record<string, FieldTypeSchema>
): OperatorType[] {
  const operators: OperatorType[] = [];
  const fieldType = fieldTypeMap[property.field_type_name];

  if (!fieldType) {
    return operators;
  }

  for (const backendOp of fieldType.operators) {
    const frontendOp = BACKEND_TO_FRONTEND_OPERATOR[backendOp];
    if (frontendOp) {
      operators.push(frontendOp);

      // Add is_not_empty when isnull is available
      if (backendOp === 'isnull') {
        operators.push('is_not_empty');
      }
    }
  }

  return operators;
}

/**
 * Check if operator needs a value input
 */
export function needsValueInput(operator: OperatorType): boolean {
  return !['is_empty', 'is_not_empty'].includes(operator);
}

/**
 * The operators a relative date token is valid under — the frontend spelling
 * of the backend's RELATIVE_OPERATORS (gte/gt/lte/lt), which report-query
 * validation enforces. Any other operator rejects the token.
 */
export const RELATIVE_DATE_OPERATORS: ReadonlySet<OperatorType> = new Set([
  'greater_than',
  'greater_than_or_equal',
  'less_than',
  'less_than_or_equal',
]);

/**
 * The value a condition keeps when its operator changes to `newOperator`.
 *
 * A value-less operator clears it. A relative date token survives only a move
 * between range operators: carried to `equals`/`contains`/... it would leave a
 * read-only token under an operator the server rejects, so the preview and
 * save fail with nothing on screen the user can edit.
 */
export function valueForOperatorChange(
  value: FilterValue,
  newOperator: OperatorType
): FilterValue {
  if (!needsValueInput(newOperator)) {
    return null;
  }
  if (isRelativeDateValue(value) && !RELATIVE_DATE_OPERATORS.has(newOperator)) {
    return null;
  }
  return value;
}

/**
 * Check if operator needs multi-select (in/not_in with options)
 */
export function needsMultiSelect(
  operator: OperatorType,
  property: PropertyTypeSchema
): boolean {
  return (
    ['in', 'not_in'].includes(operator) && (property.options?.length ?? 0) > 0
  );
}

/**
 * Substring operators take a free-text fragment, not a fully-formed value of
 * the property's type. Skip type-specific rendering and validation for these.
 *
 * contains_any/not_contains_any are deliberately excluded here — they take a
 * *list* of fragments and render via the multi-value free-text editor (see
 * isMultiTextOperator), not the single-text-input branch this flag drives.
 */
const SUBSTRING_OPERATORS: readonly OperatorType[] = [
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
];

export function isSubstringOperator(op: OperatorType): boolean {
  return SUBSTRING_OPERATORS.includes(op);
}

const MULTI_TEXT_OPERATORS: readonly OperatorType[] = [
  'contains_any',
  'not_contains_any',
];

/**
 * Multi-text operators take a list of raw substring fragments (string[])
 * rather than a single fragment or a predefined set of options — unlike
 * in/not_in on select fields, they have no options to pick from.
 */
export function isMultiTextOperator(op: OperatorType): boolean {
  return MULTI_TEXT_OPERATORS.includes(op);
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Returns true when a condition's value is present but malformed for its
 * field type — e.g. an "equals" filter on an email field with a non-email
 * value. Substring operators (contains/starts_with/…) take fragments, so we
 * don't apply type-specific format checks to them.
 */
export function isConditionValueInvalid(
  condition: FilterCondition,
  property: PropertyTypeSchema | undefined
): boolean {
  if (!property || condition.association) {
    return false;
  }
  if (
    isSubstringOperator(condition.operator) ||
    isMultiTextOperator(condition.operator)
  ) {
    return false;
  }
  if (
    condition.operator === 'is_empty' ||
    condition.operator === 'is_not_empty'
  ) {
    return false;
  }

  if (property.field_type_name === 'email') {
    // Multi-value email properties (e.g. ahoy_contact.ahoy_email) store the
    // value as an array of strings; single-value as a string.
    const candidates: string[] = [];
    if (Array.isArray(condition.value)) {
      for (const entry of condition.value) {
        if (typeof entry === 'string') {
          candidates.push(entry);
        }
      }
    } else if (typeof condition.value === 'string') {
      if (condition.operator === 'in' || condition.operator === 'not_in') {
        candidates.push(...condition.value.split(','));
      } else {
        candidates.push(condition.value);
      }
    }

    const trimmed = candidates.map(v => v.trim()).filter(v => v.length > 0);
    if (trimmed.length === 0) {
      // Empty is handled as "incomplete", not "invalid".
      return false;
    }
    return trimmed.some(v => !EMAIL_REGEX.test(v));
  }

  return false;
}

/**
 * Returns true if any condition in the list is incomplete — either the user
 * has not picked a field/association yet, or the operator requires a value
 * that has not been provided. Exported so the slideout's Apply button can
 * mirror the same disabled-state rule as the internal "Add filter" button.
 *
 * Note: this does NOT check type-level validity (e.g. email format). For
 * that, SchemaFilterBuilder reports validity via the `onValidityChange`
 * prop because the check needs the filter schema.
 */
export function hasIncompleteFilterConditions(
  conditions: FilterCondition[]
): boolean {
  return conditions.some(condition => {
    if (!condition.field && !condition.association) {
      return true;
    }
    const needsValue = needsValueInput(condition.operator);
    if (condition.association) {
      if (!needsValue) {
        return false;
      }
      if (Array.isArray(condition.value)) {
        return condition.value.length === 0;
      }
      return (
        condition.value === null ||
        condition.value === undefined ||
        String(condition.value).trim() === ''
      );
    }
    if (!needsValue) {
      return false;
    }
    if (condition.value === null || condition.value === undefined) {
      return true;
    }
    if (typeof condition.value === 'string') {
      return condition.value.trim() === '';
    }
    if (Array.isArray(condition.value)) {
      // Multi-value properties store an array; "complete" means at least one
      // non-empty entry.
      return !condition.value.some(
        v => v !== null && v !== undefined && String(v).trim() !== ''
      );
    }
    return false;
  });
}

export const renderFieldSelectEmptyState = () => (
  <p className='px-3 py-2 text-sm text-secondary'>
    No properties match your search
  </p>
);

export const MULTI_SELECT_CLASS_NAME =
  'w-full px-3 py-2 text-sm border border-secondary rounded-lg focus:outline-none focus:ring-2 ring-focus focus:border-transparent transition-colors bg-[var(--color-bg-primary)] h-20';

// Monotonic counter appended to Date.now() so two ids generated within the
// same millisecond (e.g. rapid clicks) never collide as React keys.
let filterIdCounter = 0;
export function nextFilterId(prefix: string): string {
  return `${prefix}-${Date.now()}-${filterIdCounter++}`;
}
